<template>
  <div class="p-4">
    <div class="mb-4">
      <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-1">Prototipos</h1>
      <p class="text-sm text-gray-600 dark:text-gray-400">Administra los prototipos y sus configuraciones</p>
    </div>
    
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white">Lista de Prototipos</h2>
          <div class="flex gap-2">
            <button @click="goToNewPrototipo" class="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Nuevo Prototipo
            </button>
          </div>
        </div>
        
        <!-- Filtros -->
        <form @submit.prevent="onFilter" class="flex flex-wrap gap-2 mt-3">
          <input v-model="filters.nombre" type="text" placeholder="Nombre" class="px-2 py-1.5 rounded border text-xs bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <button type="submit" class="px-3 py-1.5 rounded bg-blue-500 text-white text-xs font-semibold hover:bg-blue-600 transition">Buscar</button>
          <button type="button" @click="onClearFilters" class="px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </form>
      </div>
      
      <div class="p-4">
        <div v-if="error" class="text-red-500 text-center mb-3 text-sm">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-3 py-2 font-bold">ID</th>
                <th class="px-3 py-2 font-bold">Nombre</th>
                <th class="px-3 py-2 font-bold">Descripción</th>
                <th class="px-3 py-2 font-bold">Estado</th>
                <th class="px-3 py-2 font-bold">Negocio</th>
                <th class="px-3 py-2 font-bold">Secciones</th>
                <th class="px-3 py-2 font-bold">Costo Total</th>
                <th class="px-3 py-2 font-bold">Precios</th>
                <th class="px-3 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="prototipo in prototipos" :key="prototipo.id"
                  :class="[
                    'border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700',
                    prototipo.deshabilitado ? 'bg-gray-100 dark:bg-gray-700 opacity-70' : 'bg-white dark:bg-gray-800'
                  ]">
                <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">{{ prototipo.id }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">
                  <div class="flex items-center gap-2">
                    {{ prototipo.nombre }}
                    <span v-if="prototipo.solo_constructor" class="bg-orange-100 text-orange-800 text-xs px-1.5 py-0.5 rounded-full dark:bg-orange-900 dark:text-orange-300">
                      Solo Vista
                    </span>
                  </div>
                </td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">
                  <span class="truncate max-w-xs block" :title="prototipo.descripcion">{{ prototipo.descripcion || 'Sin descripción' }}</span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span :class="getEstadoBadgeClass(prototipo.estado)" class="px-2 py-0.5 rounded-full text-xs font-medium">
                    {{ getEstadoLabel(prototipo.estado) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <div class="flex flex-col gap-1">
                    <span class="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-indigo-900 dark:text-indigo-300">
                      {{ prototipo.negocio_origen?.nombre || 'N/A' }}
                    </span>
                    <span v-if="prototipo.es_de_otro_negocio" class="text-orange-600 dark:text-orange-400 text-xs">
                      (Otro negocio)
                    </span>
                  </div>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                    {{ prototipo.secciones_count || 0 }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="text-green-600 dark:text-green-400 font-medium text-sm">
                    ${{ parseFloat(prototipo.costo_base || 0).toFixed(2) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <button
                    v-if="!prototipo.solo_constructor"
                    @click="openPreciosModal(prototipo)"
                    class="text-green-600 hover:text-green-800 p-1 rounded transition"
                    :title="`Ver precios de ${prototipo.nombre}`"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                    </svg>
                  </button>
                  <span v-else class="text-gray-400 text-xs">N/A</span>
                </td>
                <td class="px-3 py-2 whitespace-nowrap text-right">
                  <!-- Botón Constructor (siempre disponible) -->
                  <button @click="openBuilderModal(prototipo)" class="text-purple-500 hover:text-purple-700 mr-1" title="Constructor">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                    </svg>
                  </button>

                  <!-- Botón Editar (solo si puede editar) -->
                  <button
                    v-if="prototipo.puede_editar"
                    @click="goToEditPrototipo(prototipo)"
                    class="text-blue-500 hover:text-blue-700 mr-1"
                    title="Editar"
                  >
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>

                  <!-- Botón Eliminar (solo si puede editar) -->
                  <button
                    v-if="prototipo.puede_editar"
                    @click="onDeletePrototipo(prototipo)"
                    class="text-red-500 hover:text-red-700"
                    title="Eliminar"
                  >
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>

                  <!-- Indicador para prototipos de solo vista -->
                  <span v-if="prototipo.solo_constructor" class="text-orange-500 text-xs ml-1" title="Solo puedes ver este prototipo">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </span>
                </td>
              </tr>
              <tr v-if="prototipos.length === 0">
                <td colspan="9" class="text-center py-4 text-gray-400 text-sm">No hay prototipos para mostrar.</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Paginador -->
        <Pagination 
          :current-page="currentPage" 
          :last-page="lastPage" 
          @page-changed="goToPage" 
        />
      </div>
    </div>
    
    
    <!-- Modal Constructor de Prototipo -->
    <div v-if="showBuilderModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">
                Constructor de Prototipo: {{ selectedPrototipo?.nombre }}
              </h3>
              <div class="text-sm text-green-600 dark:text-green-400 font-medium mt-1">
                Precio Total: ${{ calcularPrecioTotal().toFixed(2) }}
              </div>
            </div>
            <button @click="closeBuilderModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="flex-1 overflow-hidden flex">
          <div class="flex w-full">
            <!-- Panel de Secciones del Prototipo -->
            <div class="w-1/3 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
              <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Secciones del Prototipo</h4>
              <div class="space-y-2">
                <div 
                  v-for="seccion in prototipoCompleto?.prototipo_secciones || []" 
                  :key="seccion.id"
                  class="p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-blue-50 dark:bg-blue-900/20"
                >
                  <div class="font-medium text-gray-900 dark:text-white text-xs">{{ seccion.seccion?.nombre }}</div>
                  <div class="text-gray-500 dark:text-gray-400 text-xs">
                    Orden: {{ seccion.orden }} | {{ (seccion.prototipo_componentes || []).length }} componentes
                  </div>
                  <div class="text-green-600 dark:text-green-400 text-xs font-medium">
                    ${{ (seccion.prototipo_componentes || []).reduce((sum, comp) => sum + parseFloat(comp.costo_total || 0), 0).toFixed(2) }}
                  </div>
                </div>
              </div>
              
              <div v-if="(prototipoCompleto?.prototipo_secciones || []).length === 0" class="text-gray-400 text-center py-6 text-sm">
                No hay secciones configuradas para este prototipo.
              </div>
            </div>
            
            <!-- Panel de Componentes por Sección -->
            <div class="w-1/3 border-r border-gray-200 dark:border-gray-700 p-4 flex flex-col">
              <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex-shrink-0">Detalle de Componentes</h4>
              <div class="flex-1 overflow-y-auto">
                <div v-if="(prototipoCompleto?.prototipo_secciones || []).length > 0" class="space-y-3">
                  <div 
                    v-for="seccion in prototipoCompleto?.prototipo_secciones || []" 
                    :key="seccion.id"
                    class="border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30"
                  >
                    <div class="font-medium text-gray-900 dark:text-white text-sm p-3 border-b border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 rounded-t-lg">
                      {{ seccion.seccion?.nombre }}
                    </div>
                    <div class="p-3">
                      <div v-if="(seccion.prototipo_componentes || []).length > 0" class="space-y-1">
                        <div 
                          v-for="componente in seccion.prototipo_componentes || []" 
                          :key="componente.id"
                          class="text-xs p-2 bg-white dark:bg-gray-800 rounded border"
                        >
                          <div class="flex justify-between items-center">
                            <div>
                              <div class="font-medium text-gray-900 dark:text-white">{{ componente.componente?.nombre }}</div>
                              <div class="text-gray-500 dark:text-gray-400">
                                {{ componente.tipo_componente?.nombre }} | Cant: {{ componente.cantidad }}
                              </div>
                            </div>
                            <div class="text-green-600 dark:text-green-400 font-medium">
                              ${{ parseFloat(componente.costo_total || 0).toFixed(2) }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-gray-400 text-xs text-center py-2">
                        Sin componentes configurados
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-gray-400 text-center py-6 text-sm h-full flex items-center justify-center">
                  No hay componentes para mostrar.
                </div>
              </div>
            </div>
            
            <!-- Panel de Resumen y Acciones -->
            <div class="w-1/3 p-4 overflow-y-auto">
              <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Resumen del Prototipo</h4>
              
              <div class="space-y-3">
                <!-- Información Básica -->
                <div class="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                  <div class="text-sm font-medium text-gray-900 dark:text-white mb-2">Información General</div>
                  <div class="text-xs space-y-1 text-gray-600 dark:text-gray-400">
                    <div><strong>Nombre:</strong> {{ prototipoCompleto?.nombre }}</div>
                    <div><strong>Dimensiones:</strong> {{ prototipoCompleto?.alto }} x {{ prototipoCompleto?.ancho }} x {{ prototipoCompleto?.profundidad }} cm</div>
                    <div><strong>Estado:</strong> {{ prototipoCompleto?.estado }}</div>
                    <div v-if="prototipoCompleto?.descripcion"><strong>Descripción:</strong> {{ prototipoCompleto.descripcion }}</div>
                  </div>
                </div>

                <!-- Resumen de Costos -->
                <div class="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                  <div class="text-sm font-medium text-gray-900 dark:text-white mb-2">Resumen de Costos</div>
                  <div class="text-xs space-y-1">
                    <div v-for="seccion in prototipoCompleto?.prototipo_secciones || []" :key="seccion.id" class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">{{ seccion.seccion?.nombre }}</span>
                      <span class="font-medium text-green-600 dark:text-green-400">
                        ${{ (seccion.prototipo_componentes || []).reduce((sum, comp) => sum + parseFloat(comp.costo_total || 0), 0).toFixed(2) }}
                      </span>
                    </div>
                    <div class="border-t border-green-200 dark:border-green-800 pt-1 mt-2 flex justify-between font-medium text-green-700 dark:text-green-300">
                      <span>TOTAL</span>
                      <span>${{ calcularPrecioTotal().toFixed(2) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Estadísticas -->
                <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <div class="text-sm font-medium text-gray-900 dark:text-white mb-2">Estadísticas</div>
                  <div class="text-xs space-y-1 text-gray-600 dark:text-gray-400">
                    <div><strong>Secciones:</strong> {{ (prototipoCompleto?.prototipo_secciones || []).length }}</div>
                    <div><strong>Total Componentes:</strong> {{ (prototipoCompleto?.prototipo_secciones || []).reduce((total, seccion) => total + (seccion.prototipo_componentes || []).length, 0) }}</div>
                    <div><strong>Última actualización:</strong> {{ prototipoCompleto?.updated_at ? new Date(prototipoCompleto.updated_at).toLocaleDateString() : 'N/A' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Precios -->
    <div v-if="showPreciosModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              Precios: {{ selectedPrototipo?.nombre }}
            </h3>
            <button @click="closePreciosModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="p-4">
          <div class="mb-4">
            <button @click="showAddPrecioForm = !showAddPrecioForm" class="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar Precio
            </button>
          </div>
          
          <!-- Formulario para agregar precio -->
          <div v-if="showAddPrecioForm" class="mb-4 p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
            <form @submit.prevent="addPrecio" class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Precio *</label>
                  <input v-model="newPrecio.precio" type="number" step="0.01" required class="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm" />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Moneda</label>
                  <select v-model="newPrecio.moneda" class="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm">
                    <option value="MXN">MXN</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
                <input v-model="newPrecio.descripcion" type="text" class="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm" />
              </div>
              <div class="flex justify-end space-x-2">
                <button type="button" @click="showAddPrecioForm = false" class="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm">Cancelar</button>
                <button type="submit" :disabled="loading" class="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm">Agregar</button>
              </div>
            </form>
          </div>
          
          <!-- Lista de precios -->
          <div v-if="prototipoPrecios.length > 0">
            <table class="w-full text-xs text-left text-gray-500 dark:text-gray-400">
              <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
                <tr>
                  <th class="px-3 py-2 font-bold">Precio</th>
                  <th class="px-3 py-2 font-bold">Moneda</th>
                  <th class="px-3 py-2 font-bold">Descripción</th>
                  <th class="px-3 py-2 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="precio in prototipoPrecios" :key="precio.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">${{ precio.precio }}</td>
                  <td class="px-3 py-2 text-gray-700 dark:text-gray-200">{{ precio.moneda }}</td>
                  <td class="px-3 py-2 text-gray-700 dark:text-gray-200">{{ precio.descripcion || '-' }}</td>
                  <td class="px-3 py-2">
                    <button @click="deletePrecio(precio)" class="text-red-500 hover:text-red-700">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-gray-400 text-center py-6 text-sm">No hay precios configurados para este prototipo.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import { useUserStore } from '@/stores/user'
import Pagination from '@/components/Pagination.vue'
import Swal from 'sweetalert2'

const router = useRouter()
const { get, post, del, loading, error, data } = useHttp()
const { showSuccess, showError } = useNotifications()
const userStore = useUserStore()

// Estado principal
const prototipos = ref([])
const currentPage = ref(1)
const lastPage = ref(1)
const perPage = 12
const filters = ref({ nombre: '' })

// Modales
const showBuilderModal = ref(false)
const showPreciosModal = ref(false)
const showAddPrecioForm = ref(false)

// Formularios
const newPrecio = ref({ precio: '', moneda: 'MXN', descripcion: '' })

// Selecciones actuales
const selectedPrototipo = ref(null)
const selectedPrototipoSeccion = ref(null)

// Datos para el constructor
const prototipoSecciones = ref([])
const prototipo_componentes = ref([])
const prototipoPrecios = ref([])
const prototipoCompleto = ref(null)

// Cargar prototipos
const fetchPrototipos = async (page = 1) => {
  try {
    const params = new URLSearchParams()
    params.append('page', page)
    params.append('per_page', perPage)
    if (filters.value.nombre) params.append('nombre', filters.value.nombre)

    // Agregar negocio seleccionado si existe
    if (userStore.negocioSeleccionado?.id) {
      params.append('negocio_id', userStore.negocioSeleccionado.id)
    }

    await get(`prototipos?${params.toString()}`)
    if (data.value) {
      if (Array.isArray(data.value)) {
        prototipos.value = data.value
        currentPage.value = 1
        lastPage.value = 1
      } else {
        prototipos.value = data.value.data
        currentPage.value = data.value.current_page
        lastPage.value = data.value.last_page
      }
    }
  } catch (err) {
    prototipos.value = []
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= lastPage.value) fetchPrototipos(page)
}

const onFilter = () => { fetchPrototipos(1) }
const onClearFilters = () => { 
  filters.value = { nombre: '' }
  fetchPrototipos(1) 
}

// Navegación
const goToNewPrototipo = () => {
  router.push('/prototipos/nuevo')
}

const goToEditPrototipo = (prototipo) => {
  router.push(`/prototipos/${prototipo.id}/editar`)
}

const onDeletePrototipo = async (prototipo) => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Eliminar prototipo?',
    text: `¿Estás seguro de que deseas eliminar "${prototipo.nombre}"? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await del(`prototipos/${prototipo.id}`)
      await fetchPrototipos(currentPage.value)
      showSuccess('Prototipo eliminado exitosamente')
    } catch (err) {
      showError('No se pudo eliminar el prototipo')
    }
  }
}

// Constructor de prototipos
const openBuilderModal = async (prototipo) => {
  selectedPrototipo.value = prototipo
  showBuilderModal.value = true
  await loadPrototipoCompleto()
}

const closeBuilderModal = () => {
  showBuilderModal.value = false
  selectedPrototipo.value = null
  selectedPrototipoSeccion.value = null
  prototipoSecciones.value = []
  prototipo_componentes.value = []
  prototipoCompleto.value = null
}


const loadPrototipoCompleto = async () => {
  try {
    await get(`prototipos/${selectedPrototipo.value.id}`)
    if (data.value) {
      prototipoCompleto.value = data.value
      // Actualizar las secciones del constructor con los datos completos
      prototipoSecciones.value = data.value.prototipo_secciones || []
    }
  } catch (err) {
    showError('Error al cargar datos completos del prototipo')
  }
}

const calcularPrecioTotal = () => {
  if (!prototipoCompleto.value) return 0
  
  let total = 0
  if (prototipoCompleto.value.prototipo_secciones) {
    prototipoCompleto.value.prototipo_secciones.forEach(seccion => {
      if (seccion.prototipo_componentes) {
        seccion.prototipo_componentes.forEach(componente => {
          total += parseFloat(componente.costo_total || 0)
        })
      }
    })
  }
  return total
}


// Gestión de precios
const openPreciosModal = async (prototipo) => {
  selectedPrototipo.value = prototipo
  showPreciosModal.value = true
  await loadPrototipoPrecios()
}

const closePreciosModal = () => {
  showPreciosModal.value = false
  selectedPrototipo.value = null
  prototipoPrecios.value = []
  showAddPrecioForm.value = false
  newPrecio.value = { precio: '', moneda: 'MXN', descripcion: '' }
}

const loadPrototipoPrecios = async () => {
  try {
    await get(`prototipos/${selectedPrototipo.value.id}/precios`)
    if (data.value) prototipoPrecios.value = data.value.data || data.value
  } catch (err) {
    showError('Error al cargar precios')
  }
}

const addPrecio = async () => {
  try {
    await post(`prototipos/${selectedPrototipo.value.id}/precios`, {
      ...newPrecio.value,
      negocio_id: selectedPrototipo.value.negocio_id
    })
    showSuccess('Precio agregado exitosamente')
    newPrecio.value = { precio: '', moneda: 'MXN', descripcion: '' }
    showAddPrecioForm.value = false
    await loadPrototipoPrecios()
  } catch (err) {
    showError('Error al agregar precio')
  }
}

const deletePrecio = async (precio) => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Eliminar precio?',
    text: `¿Estás seguro de que deseas eliminar el precio de $${precio.precio} ${precio.moneda}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await del(`prototipos/${selectedPrototipo.value.id}/precios/${precio.id}`)
      showSuccess('Precio eliminado exitosamente')
      await loadPrototipoPrecios()
    } catch (err) {
      showError('Error al eliminar precio')
    }
  }
}

// Funciones para manejar estados
const getEstadoLabel = (estado) => {
  const estados = {
    'borrador': 'Borrador',
    'revision': 'En Revisión',
    'aprobado': 'Aprobado',
    'rechazado': 'Rechazado'
  }
  return estados[estado] || estado
}

const getEstadoBadgeClass = (estado) => {
  const clases = {
    'borrador': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
    'revision': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    'aprobado': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'rechazado': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
  }
  return clases[estado] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
}

onMounted(() => {
  fetchPrototipos()
})
</script>